import { safeParseFloat } from '@dte/otw-utils-core/src/numbers/safeParseFloat';
import { safeParseInt } from '@dte/otw-utils-core/src/numbers/safeParseInt';
import { ValidationError } from '@dte/otw-utils-validation/src/ValidationError';
import type { GeolocationDetails } from '../../interfaces/api/Location';
import { validateDefined } from './validateDefined';

export function validateLatitude(
	latitudeString: string | number | null | undefined,
	required?: boolean,
): number | undefined {
	const latitude = safeParseFloat(latitudeString);
	if (required) {
		validateDefined('latitude', latitude);
	}
	if (latitude === undefined) {
		return undefined;
	}
	if (latitude < -90 || latitude > 90) {
		throw new ValidationError('Latitude value must be between -90 and 90');
	}
	return latitude;
}

export function validateLongitude(
	longitudeString: string | number | null | undefined,
	required?: boolean,
): number | undefined {
	const longitude = safeParseFloat(longitudeString);
	if (required) {
		validateDefined('longitude', longitude);
	}
	if (longitude === undefined) {
		return undefined;
	}
	if (longitude < -180 || longitude > 180) {
		throw new ValidationError('Longitude value must be between -180 and 180');
	}
	return longitude;
}

export function validateLocationAccuracy(
	accuracyString: string | number | null | undefined,
	required?: boolean,
): number | undefined {
	const accuracy = safeParseInt(accuracyString);
	if (required) {
		validateDefined('location accuracy', accuracy);
	}
	if (accuracy === undefined) {
		return undefined;
	}

	if (accuracy < 0) {
		throw new ValidationError('Accuracy value must be a positive number');
	}

	return accuracy;
}

export function validateGeolocation(
	geolocation: GeolocationDetails | undefined,
	required?: boolean,
): GeolocationDetails | undefined {
	const latitude = validateLatitude(geolocation?.latitude, required);
	if (!latitude) {
		return undefined;
	}

	const longitude = validateLongitude(geolocation?.longitude, required);
	if (!longitude) {
		return undefined;
	}

	const coordinates: GeolocationDetails = {
		latitude: latitude,
		longitude: longitude,
		accuracy: validateLocationAccuracy(geolocation?.accuracy, required),
		altitude: undefined,
		altitudeAccuracy: undefined,
		heading: undefined,
		speed: undefined,
	};

	return coordinates;
}

export function isValidGeolocation(geolocation: GeolocationDetails | undefined): boolean {
	try {
		validateGeolocation(geolocation, true);
		return true;
	} catch {
		return false;
	}
}
